export const AUTH_KEY = "buff.auth.v1";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" ? window.location.origin : "http://buff.local")
).replace(/\/$/, "");

function getToken() {
  try {
    const raw = sessionStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    return JSON.parse(raw)?.api_token || null;
  } catch (_) {
    return null;
  }
}

export function getSession() {
  try {
    return JSON.parse(sessionStorage.getItem(AUTH_KEY));
  } catch (_) {
    return null;
  }
}

export function setSession(session) {
  if (!session) {
    sessionStorage.removeItem(AUTH_KEY);
    return;
  }
  sessionStorage.setItem(AUTH_KEY, JSON.stringify(session));
}

export function clearSession() {
  sessionStorage.removeItem(AUTH_KEY);
}

export class ApiError extends Error {
  constructor(message, code = null, payload = null) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.payload = payload;
  }
}

async function request(path, { method = "GET", body, formData, auth = true, quietUnauthorized = false } = {}) {
  const headers = {};
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let payload = body;
  if (formData) {
    payload = formData;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: payload,
    });
  } catch (_) {
    throw new ApiError("ارتباط با سرور برقرار نشد. اتصال اینترنت یا آدرس API را بررسی کنید.");
  }

  let json = null;
  try {
    json = await res.json();
  } catch (_) {
    throw new ApiError("پاسخ سرور نامعتبر است.");
  }

  if (res.status === 401) {
    if (!quietUnauthorized) {
      clearSession();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("buff:unauthorized"));
      }
    }
    throw new ApiError(json?.snackbar?.message || "لطفا ابتدا وارد شوید", "401", json);
  }

  if (!json?.status) {
    throw new ApiError(
      json?.snackbar?.message || json?.message || "خطا در انجام عملیات",
      json?.snackbar?.messageCode || json?.messageCode || null,
      json
    );
  }

  return json;
}

export const api = {
  login: (username, password, { quietUnauthorized = false } = {}) =>
    request("/v1/login", { method: "POST", auth: false, quietUnauthorized, body: { username, password } }),

  me: () => request("/v1/me"),

  changePassword: (data) => request("/v1/password", { method: "PUT", body: data }),

  logout: () => request("/v1/logout", { method: "POST" }),

  permissions: () => request("/v1/permissions"),

  getSettings: () => request("/v1/settings"),
  updateSettings: (data) => request("/v1/settings", { method: "PUT", body: data }),

  getGroups: () => request("/v1/groups"),
  createGroup: (data) => request("/v1/groups", { method: "POST", body: data }),
  updateGroup: (id, data) => request(`/v1/groups/${id}`, { method: "PUT", body: data }),
  deleteGroup: (id) => request(`/v1/groups/${id}`, { method: "DELETE" }),

  getPeople: (params = {}) => {
    const q = new URLSearchParams();
    if (params.group_id) q.set("group_id", params.group_id);
    const qs = q.toString();
    return request(`/v1/people${qs ? `?${qs}` : ""}`);
  },
  createPerson: (data) => request("/v1/people", { method: "POST", body: data }),
  updatePerson: (id, data) => request(`/v1/people/${id}`, { method: "PUT", body: data }),
  deletePerson: (id) => request(`/v1/people/${id}`, { method: "DELETE" }),

  getRecentFeedbacks: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "" && v !== "all") q.set(k, String(v));
    });
    const qs = q.toString();
    return request(`/v1/feedbacks/recent${qs ? `?${qs}` : ""}`);
  },
  getFeedbacks: (params = {}) => {
    const q = new URLSearchParams();
    if (params.people_id) q.set("people_id", params.people_id);
    if (params.type) q.set("type", params.type);
    if (params.applied !== undefined && params.applied !== null) q.set("applied", String(params.applied));
    const qs = q.toString();
    return request(`/v1/feedbacks${qs ? `?${qs}` : ""}`);
  },
  createFeedback: ({ people_id, type, caption, imageFile }) => {
    const fd = new FormData();
    fd.append("people_id", String(people_id));
    fd.append("type", type);
    if (caption != null) fd.append("caption", caption);
    if (imageFile) fd.append("image", imageFile);
    return request("/v1/feedbacks", { method: "POST", formData: fd });
  },
  updateFeedback: (id, data) => request(`/v1/feedbacks/${id}`, { method: "PUT", body: data }),
  deleteFeedback: (id) => request(`/v1/feedbacks/${id}`, { method: "DELETE" }),

  getAppliedBuffs: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "" && v !== "all") q.set(k, String(v));
    });
    const qs = q.toString();
    return request(`/v1/applied-buffs${qs ? `?${qs}` : ""}`);
  },
  applyBuff: (data) => request("/v1/applied-buffs", { method: "POST", body: data }),

  getUsers: () => request("/v1/users"),
  createUser: (data) => request("/v1/users", { method: "POST", body: data }),
  updateUser: (id, data) => request(`/v1/users/${id}`, { method: "PUT", body: data }),
  deleteUser: (id) => request(`/v1/users/${id}`, { method: "DELETE" }),
};

export async function loadAppState() {
  const [groupsRes, peopleRes, feedbacksRes, buffsRes, settingsRes] = await Promise.all([
    api.getGroups(),
    api.getPeople(),
    api.getFeedbacks(),
    api.getAppliedBuffs(),
    api.getSettings(),
  ]);

  const setting = settingsRes.data?.setting || {};
  const appliedBuffs = (buffsRes.data?.applied_buffs || []).map((b) => ({
    ...b,
    feedback_ids: Array.isArray(b.feedback_ids)
      ? b.feedback_ids
      : (b.feedbacks || []).map((f) => f.id),
    feedbacks: b.feedbacks || [],
  }));

  return {
    groups: groupsRes.data?.groups || [],
    people: peopleRes.data?.people || [],
    feedbacks: feedbacksRes.data?.feedbacks || [],
    appliedBuffs,
    settings: {
      negativeThreshold: Number(setting.negative_threshold) || 5,
      positiveThreshold: Number(setting.positive_threshold) || 5,
    },
  };
}

export function mapApiUser(u) {
  if (!u) return null;
  return {
    ...u,
    accessGroups: u.permissions?.manageUsers
      ? "all"
      : (u.groups || []).map((g) => g.id),
  };
}
